"use client";

import { useState } from "react";
import { ArrowRight, Loader, Mail, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inquiryTypes = [{
  value: "residential",
  label: "مسکونی"
}, {
  value: "commercial",
  label: "تجاری"
}, {
  value: "renovation",
  label: "بازسازی"
}, {
  value: "consultation",
  label: "مشاوره"
}, {
  value: "other",
  label: "سایر"
}];
const directChannels = [{
  id: 1,
  label: "تلفن",
  value: "۰۲۱-۱۲۳۴۵۶۷۸",
  icon: "phone"
}, {
  id: 2,
  label: "ایمیل",
  value: "hello@arqstudio.ir",
  icon: "mail"
}, {
  id: 3,
  label: "واتس‌اپ",
  value: "۰۹۱۲-۰۰۰-۰۰۰۰",
  icon: "message"
}];
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [selectedInquiry, setSelectedInquiry] = useState<string>("consultation");
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const [messageLength, setMessageLength] = useState<number>(0);
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-card p-12 text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
          درخواست / دریافت شد
        </p>
        <p className="font-serif text-3xl text-foreground mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest">
          ارسال درخواست جدید
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-12">
      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
        <FormField control={control} name="name" rules={{
        required: "نام الزامی است",
        minLength: {
          value: 2,
          message: "حداقل ۲ کاراکتر"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                نام و نام خانوادگی *
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="نام شما" className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "ایمیل الزامی است",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "ایمیل نامعتبر است"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                آدرس ایمیل *
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="email@example.com" className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                شماره تماس
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="۰۹۱۲-۰۰۰-۰۰۰۰" className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "لطفاً نوع پروژه را انتخاب کنید"
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                نوع پروژه *
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="مسکونی / تجاری" className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 transition-colors" value={selectedInquiry} onChange={e => {
            field.onChange(e);
            setSelectedInquiry(e.target.value);
          }} />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          نوع پروژه *
        </p>
        <ToggleGroup type="single" value={selectedInquiry} onValueChange={v => v && setSelectedInquiry(v)} className="flex flex-wrap gap-2 w-full justify-start">
          {inquiryTypes.map((type, i) => <ToggleGroupItem key={type.value} data-index={i} value={type.value} className="border border-border bg-transparent text-foreground hover:bg-muted hover:text-foreground data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] flex-none">
              {type.label}
            </ToggleGroupItem>)}
        </ToggleGroup>
      </div>

      {}
      <FormField control={control} name="message" rules={{
      required: "پیام الزامی است",
      minLength: {
        value: 10,
        message: "حداقل ۱۰ کاراکتر"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 relative">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              پیام شما *
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Textarea {...field} placeholder="درباره اهداف، زمان‌بندی یا سوالات خاص خود بنویسید..." rows={4} maxLength={1000} onChange={e => {
            field.onChange(e);
            setMessageLength(e.target.value.length);
          }} className="min-h-[140px] border-0 border-b border-border bg-transparent px-0 py-4 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 resize-none transition-colors" />
                <div className="absolute bottom-3 right-0 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {messageLength} / 1000
                </div>
              </div>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {}
      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="acknowledge" checked={acknowledged} onCheckedChange={checked => setAcknowledged(checked === true)} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
        <label htmlFor="acknowledge" className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground cursor-pointer leading-relaxed">
          من با پروتکل حریم خصوصی استودیو موافقم.
        </label>
      </div>

      {submitError && <div className="border border-destructive/50 bg-destructive/10 p-4">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting || !acknowledged} className="w-full h-14 bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-[0.25em] group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            در حال ارسال...
          </> : <>
            ارسال درخواست
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>

      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-center">
        زمان پاسخگویی / ۴ ساعت · شنبه تا پنجشنبه
      </p>
    </Form>;
}
export default function ConsultationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="2790" id="فرم-درخواست-مشاوره" className="dark relative bg-background text-foreground overflow-hidden py-32 md:py-48 px-4 sm:px-8">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[60%] bg-gradient-to-t from-primary/5 via-transparent to-transparent blur-3xl" />
      </div>

      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="max-w-[920px] mx-auto text-center mb-20 md:mb-28">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary mb-8">
            مشاوره / § ۰۰۲
          </p>
          <h2 className="font-default font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-foreground leading-[0.95] mb-10 tracking-tight">
            آغاز / یک{" "}
            <span className="font-serif italic font-normal text-foreground">
              گفتگوی خلاقانه
            </span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            هر پروژه با یک ایده شروع می‌شود. تیم ما آماده است تا با رویکردی مدرن و شخصی‌سازی شده، فضای ایده‌آل شما را طراحی کند.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-12 lg:gap-20 max-w-[1200px] mx-auto">
          <div className="lg:col-span-7">
            <FormProvider formKey="request_consultation_form_talk_with_us" formId="request_consultation_form_talk_with_us" sectionName="inquiry-form" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "consultation",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="ارسال درخواست" successMessage="درخواست شما با موفقیت دریافت شد. تیم ما به زودی با شما تماس خواهد گرفت." errorMessage="خطا در ارسال. لطفاً دوباره تلاش کنید یا مستقیماً با ما تماس بگیرید.">
              <InquiryFormContent />
            </FormProvider>
          </div>

          {}
          <aside className="lg:col-span-3 lg:pl-8 lg:border-l lg:border-border">
            <div className="sticky top-24">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-8">
                راه‌های ارتباطی
              </p>
              <div className="space-y-10">
                {directChannels.map((channel, i) => <div data-index={i} key={channel.id} className="group">
                    <div className="flex items-center gap-2 mb-3">
                      {channel.icon === "phone" && <Phone className="size-3 text-primary" />}
                      {channel.icon === "mail" && <Mail className="size-3 text-primary" />}
                      {channel.icon === "message" && <MessageCircle className="size-3 text-primary" />}
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        {channel.label}
                      </p>
                    </div>
                    <p className="font-default text-xl md:text-2xl font-light uppercase tracking-tight text-foreground hover:text-primary transition-colors cursor-pointer break-all">
                      {channel.value}
                    </p>
                  </div>)}
              </div>

              <div className="mt-12 pt-8 border-t border-border">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                  ساعات کاری استودیو
                </p>
                <p className="font-mono text-xs text-foreground/80 leading-relaxed">
                  شنبه — پنجشنبه
                  <br />
                  ۰۹:۰۰ — ۱۸:۰۰
                  <br />
                  جمعه / تعطیل
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}